import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Globe, Star, MessageCircle, Tag, Bookmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

// Directory Logo
const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

export default function DirectoryRegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '' // Empty for consumer accounts
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      // Register as consumer (no company name = consumer account)
      await register({
        ...formData,
        company_name: '', // Explicitly empty for consumer
        account_type: 'consumer'
      });
      toast.success('Account created! Welcome to the Directory.');
      navigate('/directory');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Bookmark, text: 'Bookmark your favorite businesses' },
    { icon: Star, text: 'Write reviews and ratings' },
    { icon: MessageCircle, text: 'Chat with AI Directory Assistant' },
    { icon: Tag, text: 'Access exclusive coupons & deals' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-[#0a0f1a] to-teal-900/20" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-32 left-16 w-40 h-40 border border-teal-500/30 rounded-full" />
        </div>
        
        <div className="text-center max-w-md relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <img 
              src={DIRECTORY_LOGO}
              alt="Business Directory" 
              className="w-32 h-32 mx-auto mb-6 drop-shadow-[0_0_30px_rgba(34,211,238,0.3)]"
            />
            <h1 className="font-heading text-3xl font-bold text-white mb-3">
              Join the <span className="text-cyan-400">Community</span>
            </h1>
            <p className="text-gray-400 mb-8">
              Get access to local businesses, exclusive deals, and connect with service providers in your area.
            </p>
          </motion.div>
          
          {/* Benefits */}
          <div className="space-y-4">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <benefit.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-gray-300 text-sm">{benefit.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src={DIRECTORY_LOGO}
              alt="Business Directory" 
              className="w-20 h-20 mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl font-bold text-white">Join the Community</h1>
          </div>

          <div className="bg-[#0d1525] rounded-2xl p-8 border border-white/10">
            <h2 className="font-heading text-xl font-semibold text-white mb-2">Create Your Account</h2>
            <p className="text-gray-400 text-sm mb-6">
              Join as a member to explore local businesses
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6"
                data-testid="consumer-register-submit"
              >
                {loading ? 'Creating Account...' : 'Join Now'} 
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-cyan-400 hover:underline">Log in</Link>
              </p>
            </div>

            {/* Business Link */}
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-500 text-sm mb-2">Are you a business owner?</p>
              <Link 
                to="/register" 
                className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1"
              >
                Click here to register your business <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>

          {/* Back to Directory */}
          <div className="mt-6 text-center">
            <Link to="/directory" className="text-gray-400 hover:text-white text-sm inline-flex items-center gap-1">
              <Globe className="w-4 h-4" /> Back to Directory
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
